import { useState, type FormEvent } from 'react';
import { enviosCampanaService, type RespaldoTransferencia } from '../services/enviosCampanaService';
import { useAuth } from '../context/AuthContext';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { puedeOperarDinero } from '../utils/permissions';
import { Button } from './ui/Button';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { DatePicker } from './ui/DatePicker';
import { DialogShell } from './ui/DialogShell';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { hoyEnvioChile } from './enviosCampana/accionesEnvio';
import { mensajeDeFalloAlSubir, resolverRespaldoTransferencia } from './enviosCampana/respaldoTransferencia';
import { useOperacionEnvio } from './enviosCampana/useOperacionEnvio';

interface Props {
  readonly isOpen: boolean; readonly onClose: () => void; readonly onSuccess: () => void;
  readonly campanaId: string; readonly lumisialId: string;
}
export function NotificarEnvioModal(props: Props) {
  if (!props.isOpen) return null;
  return <FormularioNotificarEnvio key={props.campanaId} {...props} />;
}
function FormularioNotificarEnvio({ onClose,onSuccess,campanaId,lumisialId }: Omit<Props, 'isOpen'>) {
  const { usuario } = useAuth();
  const operacion = useOperacionEnvio();
  const [monto,setMonto] = useState('');
  const [descripcion,setDescripcion] = useState('');
  const [fecha,setFecha] = useState(hoyEnvioChile);
  const [errorValidacion,setErrorValidacion] = useState<string | null>(null);
  const [preparando,setPreparando] = useState(false);
  // El comprobante de un envío lo revisa quien lo recibe antes de confirmar: no se objeta ni se sube después.
  const comprobante = useComprobanteFormulario({
    entidad: { tipo: 'lumisial', id: lumisialId, nombre: '' },
    fechaMovimiento: null,
    permiteSubirDespues: false,
    pideMedioPago: true,
    sentidoDinero: 'egreso',
    mensajeFaltante: 'Adjunta el comprobante de la transferencia.',
  });
  const puedeOperar = puedeOperarDinero(usuario,{ tipo:'lumisial',id:lumisialId,
    diocesisId:null,provinciaEclesiasticaId:null });
  const bloqueado = preparando || operacion.procesando || operacion.incierto || operacion.completado;
  const registrar = async (e: FormEvent) => {
    e.preventDefault();
    const valor = Number(monto);
    if (!puedeOperar || !Number.isFinite(valor) || valor<=0) {
      setErrorValidacion('Revisa el monto y el permiso de tu junta para este Lumisial.');
      return;
    }
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { setErrorValidacion(errorComprobante); return; }
    setErrorValidacion(null);
    // Se resuelve una vez: si el resultado es incierto, el reintento repite la misma operación con el mismo comprobante.
    let respaldo: RespaldoTransferencia;
    setPreparando(true);
    try {
      respaldo = await resolverRespaldoTransferencia(comprobante);
    } catch (error_: unknown) {
      setErrorValidacion(mensajeDeFalloAlSubir(error_));
      return;
    } finally { setPreparando(false); }
    const respuesta = await operacion.ejecutar(solicitudId => {
      const params = { campanaId,monto:valor,fechaEnvio:fecha,descripcion:descripcion.trim() || null,solicitudId,
        respaldo };
      return () => enviosCampanaService.crearPrimerTramo(params);
    });
    if (respuesta.estado==='confirmado') { onClose(); onSuccess(); }
  };
  return <DialogShell isOpen onClose={onClose} title="Registrar envío a JAD" size="sm"
    loading={bloqueado} onBeforeClose={() => !bloqueado}>
    <form onSubmit={registrar} className="space-y-5">
      <FormErrorAlert error={errorValidacion ?? operacion.error} />
      <ComprobanteInput comprobante={comprobante} titulo="Comprobante de la transferencia"
        deshabilitado={bloqueado} />
      <p className="text-sm text-slate-600">Registra una transferencia que ya realizaste.
        La Junta Administrativa Diocesana revisará la rendición. El dinero seguirá enviado
        aunque esa junta observe el registro.</p>
      <TextInput id="envio-monto" label="Monto enviado ($)" type="number" value={monto}
        onChange={setMonto} min={0.01} step={0.01} required disabled={bloqueado} />
      <DatePicker label="Fecha de la transferencia" value={fecha} onChange={setFecha}
        max={hoyEnvioChile()} required disabled={bloqueado} />
      <MedioPagoFolio comprobante={comprobante} deshabilitado={bloqueado} />
      <Textarea id="envio-descripcion" label="Referencia o descripción" value={descripcion}
        onChange={e => setDescripcion(e.target.value)} disabled={bloqueado} />
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" disabled={bloqueado} onClick={onClose}>Cancelar</Button>
        <Button type="submit" isLoading={operacion.procesando || preparando}
          disabled={!puedeOperar || operacion.completado || operacion.revisionObsoleta || comprobante.subiendo}>
          {operacion.incierto ? 'Verificar registro' : 'Registrar envío'}
        </Button>
      </div>
    </form>
  </DialogShell>;
}
